import type { ElementRef } from '@angular/core';
import { Component, inject, input, signal, viewChild } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxCallbackOption } from '@onyx/angular';
import {
  ActionHelper,
  OnyxDataWidgetComponent,
  OnyxDividerComponent,
  OnyxDropdownDirective,
  OnyxFormMode,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxInformationHeadingComponent,
  OnyxNamePipe,
  OnyxTooltipDirective,
} from '@onyx/angular';
import type { Subject } from 'rxjs';
import { PdfHelper } from '../../../../../../common/helpers/pdf.helper';
import { FleetIdentifierPipe } from '../../../../../../common/pipes/fleet-identifier.pipe';
import { FleetHelper } from '../../../../../fleet/common/helpers/fleet.helper';
import type { SimplifiedFleet } from '../../../../../fleet/common/interfaces/fleet';
import { ContractorHelper } from '../../../../../management-panel/contractors/common/helpers/contractor.helper';
import { OrderLoadedTrailerIconComponent } from '../../../../common/components/order-loaded-trailer-icon/order-loaded-trailer-icon.component';
import { OrderOutsourcingDriverComponent } from '../../../../common/components/order-outsourcing-driver/order-outsourcing-driver.component';
import { OrderModalHelper } from '../../../../common/helpers/order-modal.helper';
import { OrderHelper } from '../../../../common/helpers/order.helper';
import type { Order } from '../../../../common/interfaces/order';
import { OrderModalDeadlineMessageComponent } from '../../../order-modal-deadline-message/order-modal-deadline-message.component';
import { OrderModalOutsourcingFormComponent } from '../order-modal-summary-main-section/order-modal-outsourcing-form/order-modal-outsourcing-form.component';
import { OrderPdfTemplateComponent } from '../order-modal-summary-main-section/outsourcing/order-pdf-template/order-pdf-template.component';
import { ModuleLinkComponent } from '../../../../../../common/components/module-link/module-link.component';

@Component({
  selector: 'app-order-modal-outsourcing-data',
  imports: [
    ModuleLinkComponent,
    OnyxDataWidgetComponent,
    OnyxIconComponent,
    TranslatePipe,
    OnyxNamePipe,
    FleetIdentifierPipe,
    OnyxIconButtonComponent,
    OnyxTooltipDirective,
    OnyxIconComponent,
    OrderOutsourcingDriverComponent,
    OrderPdfTemplateComponent,
    OrderLoadedTrailerIconComponent,
    OnyxDropdownDirective,
    OnyxInformationHeadingComponent,
    OnyxDividerComponent,
    OrderModalOutsourcingFormComponent,
    OrderModalDeadlineMessageComponent,
  ],
  templateUrl: './order-modal-outsourcing-data.component.html',
  styleUrl: './order-modal-outsourcing-data.component.scss',
})
export class OrderModalOutsourcingDataComponent {
  private readonly fleetHelper = inject(FleetHelper);
  private readonly contractorHelper = inject(ContractorHelper);
  private readonly orderHelper = inject(OrderHelper);
  private readonly orderModalHelper = inject(OrderModalHelper);

  public readonly order = input.required<Order>();
  public readonly close$ = input.required<Subject<void>>();

  protected readonly OPTIONS: OnyxCallbackOption[] = [
    {
      name: 'labels.edit',
      value: () => this.showOutsourcingForm.set(true),
      leftIcon: { name: 'edit-outlined', size: 20 },
      leftIconColor: 'gray-alt',
    },
    {
      name: 'orders.orderModal.outsourcingForm.deleteOutsourcing',
      value: () => this.deleteOutsourcing(),
      leftIcon: { name: 'delete-outlined', size: 20 },
      leftIconColor: 'red',
    },
  ];

  protected readonly OnyxFormMode = OnyxFormMode;
  protected readonly ActionHelper = ActionHelper;

  private readonly pdfContent =
    viewChild.required<ElementRef<HTMLElement>>('pdfContent');

  protected showOutsourcingForm = signal(false);

  protected openContractorModal(): void {
    const contractor = this.order().outsourcingData?.contractor;
    if (contractor) this.contractorHelper.openModal(contractor);
  }

  protected openFleetModal(fleet: SimplifiedFleet): void {
    this.fleetHelper.openModal(fleet);
  }

  protected openActivityModal(): void {
    this.orderModalHelper.openActivityModal({ order: this.order() });
  }

  protected createPdf(): void {
    const fileName = `${this.order().identifier}.pdf`;
    PdfHelper.createPdf(this.pdfContent().nativeElement, fileName);

    // TODO: ONYX-1955
    // const fileName = `${this.order().identifier}.pdf`;
    // const document = PdfHelper.createPdf(
    //   this.pdfContent().nativeElement,
    //   fileName,
    // );
    // const file = new File([document], fileName, {
    //   type: document.type,
    //   lastModified: DateTime.now().toMillis(),
    // });

    // this.storageService
    //   .uploadFile(file)
    //   .pipe(
    //     switchMap((documentUuid) => {
    //       const form = OrderFormHelper.fromDto(this.order());
    //       const baseDto = this.orderFormService.getDto(form);

    //       const dto: OrderFormDto = {
    //         ...baseDto,
    //         documents: [
    //           ...baseDto.documents,
    //           { name: fileName, attachments: [documentUuid], note: null },
    //         ],
    //       };

    //       return this.ordersService.editOrder(this.order().uuid, dto);
    //     }),
    //   )
    //   .subscribe();
  }

  private deleteOutsourcing(): void {
    const order = this.order();
    if (order) this.orderHelper.deleteOutsourcing(order);
  }
}
